import moment from "moment";
import type {
  ApiIntegrationTimeline,
  ApiPartnerIntegration,
  ApiPartnerRow,
  ApiPartnerTechnology,
  ApiTechnologyStat,
  ApiTimelinePhase,
  PartnerIntegrationStatusAPIResponse,
} from "./Types";
import {
  mapTableColumnsFromTechnologies,
  mergeTableColumns,
  partnerTechnologyMatchesColumn,
  technologyToSlug,
  type IntegrationTableColumn,
} from "./integrationStatusColumns";
import {
  INTEGRATION_SUMMARY_TILE_CONFIG,
  NOT_APPLICABLE_PROGRESS,
  type IntegrationCellStatus,
  type IntegrationEndorsement,
  type IntegrationMilestone,
  type IntegrationPartnerRow,
  type IntegrationPlatformCard,
  type IntegrationPlatformDetail,
  type IntegrationPlatformProgress,
  type IntegrationPlatformProgressRow,
  type IntegrationProjectDetail,
  type IntegrationSummaryTile,
} from "../../../pages/hid-admin/dashboard/partner-organizations/partner-list/integration-status/integrationStatusTypes";
import {
  isNotApplicableStatus,
  mapPhaseToMilestoneState,
  NOT_APPLICABLE_META_LABEL,
} from "../../../common/components/integration-timeline/timelineUtils";
import { formatConsultantName } from "../../../utils/formatConsultantName";

const MILESTONE_LABELS = ["Onboarding", "Training", "Development", "Validation"] as const;
const MILESTONE_KEYS: Array<keyof ApiIntegrationTimeline> = [
  "onboarding",
  "training",
  "development",
  "validation",
];

const createEmptyPlatformProgressRow = (
  tableColumns: IntegrationTableColumn[]
): IntegrationPlatformProgressRow =>
  Object.fromEntries(tableColumns.map((col) => [col.key, NOT_APPLICABLE_PROGRESS]));

const mapTechnologyIntegrationStatus = (
  integrationStatus: string
): IntegrationCellStatus => {
  const lower = integrationStatus.toLowerCase().trim();
  if (lower === "completed") return "completed";
  if (lower === "not applicable") return "not-applicable";
  return "not-completed";
};

const formatTimelineDate = (date: string | null | undefined): string | undefined => {
  if (!date || date.trim() === "" || date.startsWith("0000-")) {
    return undefined;
  }
  const parsed = moment(date, ["YYYY-MM-DD", moment.ISO_8601], true);
  if (!parsed.isValid() || parsed.year() <= 0) {
    return undefined;
  }
  return parsed.format("DD MMM YYYY");
};

const mapPhaseToMilestone = (
  label: string,
  phase: ApiTimelinePhase
): IntegrationMilestone => {
  const state = mapPhaseToMilestoneState(phase.status);
  const completedDate =
    state === "completed" ? formatTimelineDate(phase.completion_date) : undefined;
  const statusDate =
    state === "in-progress" || state === "on-hold"
      ? formatTimelineDate(phase.start_date) ?? formatTimelineDate(phase.completion_date)
      : undefined;

  return {
    label,
    state,
    ...(isNotApplicableStatus(phase.status)
      ? { statusLabel: NOT_APPLICABLE_META_LABEL }
      : {}),
    ...(completedDate ? { completedDate } : {}),
    ...(statusDate ? { statusDate } : {}),
  };
};

const mapValidationStatus = (timeline: ApiIntegrationTimeline): IntegrationCellStatus => {
  const validationStatus = timeline.validation?.status ?? "";
  const lower = validationStatus.toLowerCase().trim();
  if (lower === "completed") return "completed";
  if (lower === "not applicable" || lower === "notapplicable") return "not-applicable";
  return "not-completed";
};

const capitalizeWords = (value: string): string =>
  value
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const normalizeStatusPart = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, " ").trim();

const parseCurrentStatus = (
  currentStatus: string
): Pick<IntegrationProjectDetail, "currentStatus" | "currentStatusSubTask"> => {
  const trimmed = currentStatus.trim();
  if (!trimmed) {
    return { currentStatus: "not-started" };
  }

  const lower = normalizeStatusPart(trimmed);
  if (lower === "completed") {
    return { currentStatus: "completed" };
  }
  if (lower === "not started") {
    return { currentStatus: "not-started" };
  }
  if (lower === "not applicable") {
    return { currentStatus: "not-applicable" };
  }

  const colonIndex = trimmed.indexOf(":");
  if (colonIndex >= 0) {
    const subTask = capitalizeWords(trimmed.slice(0, colonIndex).trim());
    const statusPart = normalizeStatusPart(trimmed.slice(colonIndex + 1));

    if (statusPart === "in progress") {
      return { currentStatus: "in-progress", currentStatusSubTask: subTask };
    }
    if (statusPart === "on hold") {
      return { currentStatus: "on-hold", currentStatusSubTask: subTask };
    }
    if (statusPart === "completed") {
      return { currentStatus: "stage-completed", currentStatusSubTask: subTask };
    }
    if (statusPart === "not started") {
      return { currentStatus: "not-started", currentStatusSubTask: subTask };
    }
    if (statusPart === "not applicable") {
      return { currentStatus: "not-applicable", currentStatusSubTask: subTask };
    }
  }

  return { currentStatus: "not-started" };
};

const mapEndorsement = (endorsement: string): IntegrationEndorsement => {
  const trimmed = endorsement.trim();
  if (!trimmed || trimmed === "-") {
    return { label: "—", variant: "neutral" };
  }
  if (trimmed.toLowerCase() === "fully endorsed") {
    return { label: "Fully Endorsed", variant: "success" };
  }
  if (trimmed.toLowerCase() === "not endorsed") {
    return { label: "Not Endorsed", variant: "danger" };
  }
  return { label: trimmed, variant: "neutral" };
};

const mapIntegrationToProject = (
  integration: ApiPartnerIntegration,
  projectName: string,
  consultantName?: string
): IntegrationProjectDetail => {
  const milestones = MILESTONE_KEYS.map((key, index) =>
    mapPhaseToMilestone(MILESTONE_LABELS[index], integration.timeline[key])
  );

  return {
    serviceName: projectName,
    provisioningType: integration.integration_name,
    technicalConsultant: consultantName,
    milestones,
    ...parseCurrentStatus(integration.current_status),
    age: integration.age,
    validationStatus: mapValidationStatus(integration.timeline),
    endorsement: mapEndorsement(integration.endorsement),
    score: integration.score ?? 0,
  };
};

const mapTechnologyToPlatformProgress = (
  technologies: ApiPartnerTechnology[]
): IntegrationPlatformProgress => {
  if (technologies.length === 0) {
    return { status: "not-applicable" };
  }

  const statuses = technologies.map((tech) =>
    mapTechnologyIntegrationStatus(tech.integration_status)
  );

  if (statuses.every((status) => status === "not-applicable")) {
    return { status: "not-applicable" };
  }

  const completedCount = technologies.reduce(
    (sum, tech) => sum + (tech.completed_integrations ?? 0),
    0
  );
  const totalCount = technologies.reduce(
    (sum, tech) => sum + (tech.total_integrations ?? 0),
    0
  );
  const displayLabel = technologies[0]?.completed_label ?? "";
  const status: IntegrationCellStatus =
    completedCount > 0 ? "completed" : "not-completed";

  return {
    status,
    displayLabel,
    completedCount,
    totalCount,
  };
};

const buildExpandedDetails = (
  technologies: ApiPartnerTechnology[],
  tableColumns: IntegrationTableColumn[]
): IntegrationPlatformDetail[] => {
  const details: IntegrationPlatformDetail[] = [];

  for (const column of tableColumns) {
    const matchingTechnologies = technologies.filter((tech) =>
      partnerTechnologyMatchesColumn(tech.technology, column.label)
    );

    for (const tech of matchingTechnologies) {
      const projects: IntegrationProjectDetail[] = [];

      for (const project of tech.projects) {
        if (!project.integrations?.length) continue;

        for (const integration of project.integrations) {
          projects.push(
            mapIntegrationToProject(
              integration,
              project.project_name,
              formatConsultantName(project.consultant_details)
            )
          );
        }
      }

      if (projects.length > 0) {
        details.push({
          platformKey: technologyToSlug(tech.technology),
          columnKey: column.key,
          platformLabel: tech.technology,
          totalProjects: tech.total_projects ?? 0,
          projects,
        });
      }
    }
  }

  return details;
};

const buildPlatformProgress = (
  technologies: ApiPartnerTechnology[],
  tableColumns: IntegrationTableColumn[]
): IntegrationPlatformProgressRow => {
  const row = createEmptyPlatformProgressRow(tableColumns);

  for (const column of tableColumns) {
    const matchingTechnologies = technologies.filter((tech) =>
      partnerTechnologyMatchesColumn(tech.technology, column.label)
    );

    if (matchingTechnologies.length === 0) continue;

    row[column.key] = mapTechnologyToPlatformProgress(matchingTechnologies);
  }

  return row;
};

const mapTableColumns = (
  response: PartnerIntegrationStatusAPIResponse
): IntegrationTableColumn[] =>
  mapTableColumnsFromTechnologies(
    (response.technologies_stats ?? []).map((stat) => stat.technology)
  );

const mapSummaryTiles = (
  response: PartnerIntegrationStatusAPIResponse
): IntegrationSummaryTile[] => {
  const values: Record<string, number> = {
    overall: response.completed_integrations ?? 0,
    "completed-year": response.completed_this_year ?? 0,
    "in-progress": response.in_progress ?? 0,
    "on-hold": response.on_hold ?? 0,
  };

  return INTEGRATION_SUMMARY_TILE_CONFIG.map((tile) => ({
    ...tile,
    value: values[tile.id] ?? 0,
  }));
};

const mapPlatformCards = (
  response: PartnerIntegrationStatusAPIResponse
): IntegrationPlatformCard[] =>
  (response.technologies_stats ?? []).map((stat: ApiTechnologyStat) => ({
    id: technologyToSlug(stat.technology),
    name: stat.technology,
    integrationCount: stat.integrations ?? 0,
    completionPercent: stat.percentage ?? 0,
    done: stat.done ?? 0,
    active: stat.active ?? 0,
    hold: stat.hold ?? 0,
  }));

const mapPartnerRow = (
  partner: ApiPartnerRow,
  tableColumns: IntegrationTableColumn[]
): IntegrationPartnerRow => ({
  id: String(partner.partner_id),
  partnerId: partner.partner_id,
  partnerName: partner.partner_name,
  platformProgress: buildPlatformProgress(partner.technologies ?? [], tableColumns),
  expandedDetails: buildExpandedDetails(partner.technologies ?? [], tableColumns),
});

export const mapPartnerIntegrationStatusResponse = (
  response: PartnerIntegrationStatusAPIResponse,
  existingTableColumns: IntegrationTableColumn[] = []
) => {
  const tableColumns = mergeTableColumns(
    existingTableColumns,
    mapTableColumns(response)
  );

  return {
    summaryTiles: mapSummaryTiles(response),
    platformCards: mapPlatformCards(response),
    tableColumns,
    partners: (response.data ?? []).map((partner) => mapPartnerRow(partner, tableColumns)),
    pagination: response.pagination,
  };
};
